import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ralToHex } from "../../helpers/RalToHex";

const UI_CATEGORIES = [
  { id: "unies", name: "Couleurs unies", sourceIds: ["brillants", "mats"] },
  { id: "metal", name: "Métal", sourceIds: ["metal"] },
  { id: "bois", name: "Effet bois", sourceIds: ["bois"] },
];

const TextureForm = ({
  selectedMaterial,
  onMaterialChange = () => null,
  textures,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const [selectedUiCategory, setSelectedUiCategory] = useState("unies");
  const [previewColor, setPreviewColor] = useState(null);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [finishByColor, setFinishByColor] = useState({});

  // Sync finish selection from parent `selectedMaterial` so that
  // when user comes back to this step we keep the chosen finish
  // (mat or brillant) for the matching base color.
  useEffect(() => {
    if (!selectedMaterial || selectedUiCategory !== "unies" || !textures)
      return;

    try {
      const brillantsSource = textures.find((t) => t.id === "brillants");

      // selectedMaterial.materialName holds the base color name
      const selectedName = selectedMaterial?.materialName;
      if (!selectedName) return;

      // keys in colorsForSelected are now brillants colors
      const brillantColor = brillantsSource?.colors?.find((c) => c.name === selectedName);

      if (brillantColor) {
        setFinishByColor((prev) => {
          // don't overwrite an explicit user selection in this session
          if (prev[brillantColor.id]) return prev;
          const mapped =
            selectedMaterial.materialType === "mats" ? "mat" : "brillant";
          return { ...prev, [brillantColor.id]: mapped };
        });
      }
    } catch (err) {
      console.error("Error syncing finish from selectedMaterial", err);
    }
  }, [selectedMaterial, textures, selectedUiCategory]);

  const getSourceById = (id) => textures.find((t) => t.id === id);

  const colorsForSelected = useMemo(() => {
    const uiCat = UI_CATEGORIES.find((c) => c.id === selectedUiCategory);
    if (!uiCat) return [];

    const seen = new Set();
    const merged = [];

    if (uiCat.id === "unies") {
      // Use brillants as the source (defines the display order)
      ["brillants"]
        .map(getSourceById)
        .filter(Boolean)
        .forEach((src) => {
          (src.colors || []).forEach((c) => {
            if (c?.active && !seen.has(c.id)) {
              merged.push(c);
              seen.add(c.id);
            }
          });
        });
    } else {
      uiCat.sourceIds
        .map(getSourceById)
        .filter(Boolean)
        .forEach((src) => {
          (src.colors || []).forEach((c) => {
            if (c?.active && !seen.has(c.id)) {
              merged.push(c);
              seen.add(c.id);
            }
          });
        });
    }

    return merged;
  }, [selectedUiCategory, getSourceById, textures]);

  const handleUiCategoryChange = (uiId) => {
    setSelectedUiCategory(uiId);
    setPreviewColor(null);
    setFinishDialogOpen(false);
  };

  const handleColorClick = (color) => {
    if (selectedUiCategory === "unies") {
      setPreviewColor(color);
      setFinishDialogOpen(true);
      return;
    }
    onMaterialChange(color.id);
  };

  const validateFinish = (matType) => {
    if (!previewColor) return;

    // displaying "brillants" colors by default, so only change ID when user selected mat colors
    const finalMaterialId =
      matType === "brillant"
        ? previewColor?.id
        : textures
            .find((t) => t.id === "mats")
            ?.colors?.find((c) => c.name === previewColor?.name)?.id;

    if (!finalMaterialId) {
      console.error("Failed to get color reference");
      return;
    }

    onMaterialChange?.(finalMaterialId);
    setFinishByColor((prev) => ({ ...prev, [previewColor.id]: matType }));
    setFinishDialogOpen(false);
    return;
  };

  const isSelectedColor = (color) => {
    return (
      selectedMaterial?.materialName === color?.name
    ); /* used to be selectedMaterial === color.id before base color tabs grouping */
  };

  return (
    <Box
      sx={{
        paddingRight: "20px",
        paddingLeft: "20px",
        minHeight: isXs ? "15vh" : "auto",
      }}
    >
      <Typography
        variant="h3"
        gutterBottom
        align="center"
        sx={{
          fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem", lg: "2rem" },
          paddingBottom: { xs: "0px", md: "30px" },
        }}
      >
        Choisir mon coloris
      </Typography>

      {/* Boutons catégories UI (Unies / Métal / Bois) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          mb: 3,
        }}
      >
        {UI_CATEGORIES.map((cat) => (
          <Button
            key={cat.id}
            onClick={() => handleUiCategoryChange(cat.id)}
            sx={{
              padding: isXs ? "5px 10px" : "10px 50px",
              minWidth: 0,
              height: "40px",
              textTransform: "none",
              backgroundColor:
                selectedUiCategory === cat.id
                  ? theme.palette.custom?.btnBackground ||
                    theme.palette.primary.main
                  : "white",
              color: selectedUiCategory === cat.id ? "white" : "black",
              border: `1px solid ${
                theme.palette.custom?.btnBackground ||
                theme.palette.primary.main
              }`,
              borderRadius: "50px",
              transition: "background-color 0.3s, color 0.3s",
              "&:hover": {
                backgroundColor:
                  selectedUiCategory === cat.id
                    ? theme.palette.custom?.btnBackground ||
                      theme.palette.primary.main
                    : theme.palette.custom?.blue || theme.palette.primary.main,
                color: "white",
              },
              flex: "1 1 auto",
              fontSize: isXs ? "0.8rem" : "1rem",
            }}
          >
            {cat.name}
          </Button>
        ))}
      </Box>

      {/* Grille de couleurs */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          paddingBottom: isXs ? undefined : "20px",
        }}
      >
        {colorsForSelected.map((color) => (
          <Box
            key={color.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: isXs ? "80px" : "130px",
            }}
          >
            <CardActionArea
              onClick={() => color.badge !== "outOfStock" && handleColorClick(color)}
              disabled={color.badge === "outOfStock"}
              sx={{
                borderRadius: 2,
                border: isSelectedColor(color)
                  ? "3px solid #1976d2"
                  : "1px solid #e0e0e0",
                transition: "box-shadow 0.3s ease-in-out, transform 0.2s",
                width: "100%",
                height: isXs ? "80px" : "130px",
                flex: "0 0 auto",
                aspectRatio: "1 / 1",
                position: "relative",
                overflow: "hidden",
                cursor: color.badge === "outOfStock" ? "not-allowed" : "pointer",
                "&.Mui-disabled": {
                  opacity: 1,
                },
              }}
            >
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "5px",
                  overflow: "hidden",
                  backgroundImage:
                    color.colorType === "TEXTURE"
                      ? `url(${color.thumbnailUrl})`
                      : "none",
                  backgroundColor:
                    color.colorType === "HEX"
                      ? `#${color.color?.replace(/^#/, "")}`
                      : color.ralColorId
                      ? ralToHex(color.ralColorId, " Card")
                      : "transparent",
                  backgroundSize:
                    color.colorType === "TEXTURE" ? "cover" : "initial",
                  backgroundPosition:
                    color.colorType === "TEXTURE" ? "center" : "initial",
                }}
              />

              {/* Overlay gris pour rupture de stock */}
              {color.badge === "outOfStock" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(128, 128, 128, 0.6)",
                    borderRadius: "5px",
                    zIndex: 5,
                  }}
                />
              )}

              {/* Bandeau diagonal Nouveau ou Rupture de stock */}
              {color.badge && (
                <Box
                  sx={{
                    position: "absolute",
                    top: isXs ? "12px" : "18px",
                    left: isXs ? "-28px" : "-35px",
                    width: isXs ? "90px" : "120px",
                    backgroundColor: color.badge === "new" ? "#4caf50" : "#d32f2f",
                    color: "white",
                    fontSize: isXs ? "8px" : "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    textAlign: "center",
                    padding: isXs ? "3px 0" : "4px 0",
                    transform: "rotate(-45deg)",
                    zIndex: 10,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    letterSpacing: "0.5px",
                  }}
                >
                  {color.badge === "new" ? "Nouveau" : "Rupture"}
                </Box>
              )}

              {/* Texte sur la couleur sélectionnée */}
              {isSelectedColor(color) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    width: "90%",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      textAlign: "center",
                      fontStyle: "italic",
                      fontSize: isXs ? "10px" : "13px",
                      color: color.whiteText ? "white" : "black",
                      wordBreak: "break-word",
                    }}
                  >
                    {color.name}
                    {selectedUiCategory === "unies" && (
                      <>{" "}{finishByColor[color.id] === "mat" ? "Mat" : "Brillant"}</>
                    )}
                  </Typography>
                  {color.subname?.trim() && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        textAlign: "center",
                        fontStyle: "italic",
                        fontSize: isXs ? "10px" : "13px",
                        color: color.whiteText ? "white" : "black",
                      }}
                    >
                      ({color.subname})
                    </Typography>
                  )}
                </Box>
              )}
            </CardActionArea>

            {/* Texte sous la couleur non sélectionnée */}
            {!isSelectedColor(color) && (
              <Box
                sx={{
                  textAlign: "center",
                  mt: 1,
                  height: isXs ? "32px" : "40px",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "bold",
                    fontFamily:
                      "'Fira Sans', Helvetica, Arial, Lucida, sans-serif",
                    textTransform: "uppercase",
                    fontSize: isXs ? "11px" : "13px",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {color.name}
                </Typography>
                {color.subname?.trim() && !isXs && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: "bold",
                      fontFamily:
                        "'Fira Sans', Helvetica, Arial, Lucida, sans-serif",
                      textTransform: "uppercase",
                      fontSize: "12px",
                      lineHeight: 1.2,
                    }}
                  >
                    ({color.subname})
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Dialog de choix de finition pour “Couleurs unies” */}
      <Dialog
        open={finishDialogOpen}
        onClose={() => setFinishDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontFamily: "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
            color: "#007ac2",
            fontWeight: 600,
          }}
        >
          Choisir la finition
        </DialogTitle>
        <DialogContent>
          {previewColor && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, textTransform: "uppercase", fontWeight: 700 }}
              >
                {previewColor.name}
                {previewColor.subname?.trim()
                  ? ` (${previewColor.subname})`
                  : ""}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="stretch"
                sx={{ mt: 1 }}
              >
                {/* Brillant */}
                <CardActionArea
                  onClick={() => validateFinish("brillant")}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Card
                    sx={{
                      height: 120,
                      borderRadius: 1.5,
                      backgroundImage:
                        previewColor.colorType === "TEXTURE"
                          ? `url(${previewColor.thumbnailUrl})`
                          : "none",
                      backgroundColor:
                        previewColor.colorType === "HEX"
                          ? `#${previewColor.color?.replace(/^#/, "")}`
                          : previewColor.ralColorId
                          ? ralToHex(previewColor.ralColorId, " DialogBrillant")
                          : "transparent",
                      backgroundSize:
                        previewColor.colorType === "TEXTURE"
                          ? "cover"
                          : "initial",
                      backgroundPosition:
                        previewColor.colorType === "TEXTURE"
                          ? "center"
                          : "initial",
                      overflow: "hidden",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.3) 10%, rgba(255,255,255,0) 40%)",
                        pointerEvents: "none",
                      },
                      boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                    }}
                  />
                  <Typography
                    align="center"
                    sx={{ mt: 1, fontWeight: 700, textTransform: "uppercase" }}
                  >
                    Brillant
                  </Typography>
                </CardActionArea>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />

                {/* Mat */}
                <CardActionArea
                  onClick={() => validateFinish("mat")}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Card
                    sx={{
                      height: 120,
                      borderRadius: 1.5,
                      backgroundImage:
                        previewColor.colorType === "TEXTURE"
                          ? `url(${previewColor.thumbnailUrl})`
                          : "none",
                      backgroundColor:
                        previewColor.colorType === "HEX"
                          ? `#${previewColor.color?.replace(/^#/, "")}`
                          : previewColor.ralColorId
                          ? ralToHex(previewColor.ralColorId, " DialogMat")
                          : "transparent",
                      backgroundSize:
                        previewColor.colorType === "TEXTURE"
                          ? "cover"
                          : "initial",
                      backgroundPosition:
                        previewColor.colorType === "TEXTURE"
                          ? "center"
                          : "initial",
                      filter:
                        // petit effet "mat" (UI only)
                        "saturate(0.97) brightness(0.98)",
                      boxShadow: "inset 0 0 6px rgba(0,0,0,0.15)",
                    }}
                  />
                  <Typography
                    align="center"
                    sx={{ mt: 1, fontWeight: 700, textTransform: "uppercase" }}
                  >
                    Mat
                  </Typography>
                </CardActionArea>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TextureForm;
